import { useTranslation } from "react-i18next";
import { FormikProps } from "formik";
import { Typography } from "@mui/material";
import ContactSearchAutoComplete from "components/common/ContactSearchAutoComplete";
import ContactStatus from "enums/ContactStatus";
import LatestPayments from "./LatestPayments";
import {
  formatPayToForPayment,
  FormikPayment,
} from "utils/payments/paymentFormat";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import {
  LocalImageAsset,
  OcrInvoiceResult,
} from "hooks/useAttachmentAndAssetHandling";
import AttachmentsComponent, {
  AttachmentRenderType,
} from "components/common/attachments/AttachmentComponent";
import { AttachmentDrawerType } from "enums/AttachmentDrawerType";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import { useEffect, useState } from "react";
import ExternalLink from "../common/ExternalLink";
import Summary, { SummaryItem } from "components/common/Summary";
import { Divider } from "@mui/material";
import { Stack } from "components/layout/reusableLayouts";

interface StartPaymentDetailsProps {
  formikPayment: FormikProps<FormikPayment>;
  ocrResult: OcrInvoiceResult | null;
  paymentContactMissingBankErrorMessage: React.ReactNode | null;
  refetchSelectedPayToContactIndex: number;
  handleLocalFileUpload: (newAssets: LocalImageAsset[]) => void;
  handleNext: () => void;
  setOcrResult: (result: OcrInvoiceResult | null) => void;
  resetFormWithPayment: (payment: Partial<FormikPayment>) => void;
}

const StartPaymentDetails = (props: StartPaymentDetailsProps) => {
  const {
    formikPayment,
    ocrResult,
    paymentContactMissingBankErrorMessage,
    refetchSelectedPayToContactIndex,
    handleLocalFileUpload,
    handleNext,
    setOcrResult,
    resetFormWithPayment,
  } = props;
  const { t } = useTranslation();

  const [pendingNext, setPendingNext] = useState(false);

  const { handleFormikHelperText } = useFormikHelpers(formikPayment);

  const prefillPaymentFromOCR = (ocrData: OcrInvoiceResult) => {
    let dueDate: Date | null = null;
    if (ocrData.dueDate) {
      const parsed = new Date(ocrData.dueDate);
      dueDate = isNaN(parsed.getTime()) ? null : parsed;
    } else if (
      ocrData.paymentTerms &&
      ocrData.paymentTerms.toLowerCase().includes("due upon receipt")
    ) {
      dueDate = new Date();
    }

    let amount = 0;
    if (ocrData.amount) {
      amount = parseFloat(ocrData.amount.replace(/[^0-9.]/g, "")) || 0;
    }

    formikPayment.setFieldValue("dueDate", dueDate);
    formikPayment.setFieldValue("amount", amount);
  };

  useEffect(() => {
    if (pendingNext && formikPayment.values.payTo?.id) {
      setPendingNext(false);
      handleNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingNext]);

  // Scanned values are data, and `Summary` runs `t()` over any string value, so
  // a merchant named "Name" would render as the translation of that key. A node
  // is passed through untouched.
  const scanned = (value?: string) =>
    value ? <Typography variant="body1">{value}</Typography> : undefined;

  const ocrSummaryItems: SummaryItem[] = [
    {
      title: t("Amount"),
      value: scanned(ocrResult?.amount && `$${ocrResult.amount}`),
    },
    { title: t("Due Date"), value: scanned(ocrResult?.dueDate) },
    { title: t("Name"), value: scanned(ocrResult?.merchantName) },
    { title: t("Phone"), value: scanned(ocrResult?.merchantPhone) },
    {
      title: t("URL / Website"),
      fullWidth: true,
      value: ocrResult?.merchantUrl && (
        <ExternalLink to={ocrResult.merchantUrl}>
          {ocrResult.merchantUrl}
        </ExternalLink>
      ),
    },
  ];

  return (
    <Stack gap="var(--space-4)">
      {!ocrResult && (
        <>
          <Typography variant="h5">
            {t("How do you want to start this payment?")}
          </Typography>
          <Stack gap="var(--space-1)">
            <Typography variant="h6">{t("Upload a Bill")}</Typography>
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {t("We'll scan the file to prefill payment details")}
            </Typography>
          </Stack>

          <AttachmentsComponent
            drawerType={AttachmentDrawerType.ATTACHMENT}
            renderType={AttachmentRenderType.ACCORDION}
            parentDetails={{
              parentId: "",
              parentType: ATTACHMENT_PARENT_TYPE.PAYMENT,
            }}
            onAddAssets={handleLocalFileUpload}
            dropZoneOnly
            uploadFileToOcr={true}
            onOcrResult={(ocrData) => {
              prefillPaymentFromOCR(ocrData);
              setOcrResult(ocrData);
            }}
          />
        </>
      )}

      {ocrResult && (
        <div>
          <Typography
            variant="body2"
            sx={{ paddingBlockEnd: "var(--space-6)" }}
          >
            {t("ContactNoMatchOCRDescription")}
          </Typography>

          <Typography variant="h6" sx={{ paddingBlockEnd: "var(--space-4)" }}>
            {t("Payment Details")}
          </Typography>

          <Summary items={ocrSummaryItems} />
        </div>
      )}

      {!ocrResult && (
        <Divider sx={{ marginBlock: "var(--space-4)" }}>
          <Typography variant="overline">{t("OR")}</Typography>
        </Divider>
      )}

      <Stack gap="var(--space-8)">
        <Stack gap="var(--space-3)">
          <Typography variant="caption">{t("Pay to Contact")}</Typography>

          <ContactSearchAutoComplete
            key={formikPayment.values.payTo?.id || "new-payment"}
            onSelectContact={(recipient) => {
              const currentPayToId = formikPayment.values.payTo?.id;

              if (recipient && !Array.isArray(recipient)) {
                if (recipient.id !== currentPayToId) {
                  formikPayment.setFieldValue(
                    "payTo",
                    formatPayToForPayment(recipient),
                  );
                }
              } else if (!recipient && currentPayToId) {
                formikPayment.setFieldValue(
                  "payTo",
                  formatPayToForPayment(null),
                );
              }
            }}
            contactId={formikPayment.values.payTo?.id}
            customPlaceholder={
              ocrResult
                ? t("Contact not matched, search for contact")
                : t("Search for a contact")
            }
            status={ContactStatus.Active}
            forcePickBusiness={true}
            hideLabel={true}
            showAddContactInDropdown={true}
            handleBlur={() => formikPayment.setFieldTouched("payTo.id", true)}
            errorText={
              paymentContactMissingBankErrorMessage ||
              handleFormikHelperText("payTo.id")
            }
            suppressOnSelectOnExternalChange={pendingNext}
            refetchIndex={refetchSelectedPayToContactIndex}
          />
        </Stack>

        {!ocrResult && (
          <LatestPayments
            title={t("Recently Paid")}
            description={t("Select a recent payment to prefill details")}
            contactId={formikPayment.values.payTo?.id}
            onSelectPayment={(payment) => {
              setPendingNext(true);
              const { id, status, actualPaymentDate, customFields, ...rest } =
                payment;
              resetFormWithPayment({
                id: "",
                status: undefined,
                actualPaymentDate: undefined,
                ...rest,
              });
            }}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default StartPaymentDetails;
